import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { RequestHandlerScreen } from 'common-src/components/base';
import {
  cachePortalThreadMessages,
  cachePortalThreads,
  getCachedPortalThreads,
} from 'common-src/features/cache';
import { apiRequest, getRestErrorMessage } from 'common-src/features/rest';
import { openDocument } from 'common-src/models/PatientDocument';

import { ActionsRow, BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import BasePopup from 'src/popups/BasePopup';

import styles from './SecureMessagingAll.module.scss';

const TableHeaders = [
  { column: 'subject', label: 'Subject', width: '35%', sortBy: 'text' },
  { column: 'recipients', label: 'Recipients', width: '25%', sortBy: 'text' },
  { column: 'sentAt', label: 'Sent At', width: '20%', sortBy: 'value' },
  { column: 'actions', label: 'Actions', width: '20%' },
];

const getNormalizedThreads = (items) =>
  items
    .map((item) => {
      const recipients = [];
      if (item.sendToPatient ?? item.send_to_patient) recipients.push('Patient');
      if (item.sendToCaregiver ?? item.send_to_caregiver) recipients.push('Caregiver');

      const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
      const sentAtValue =
        createdAt && moment(createdAt).isValid() ? moment(createdAt).valueOf() : 0;

      return {
        threadId: item.threadId ?? item.thread_id ?? item.id,
        subject: item.messageSubject ?? item.message_subject ?? '-',
        body: item.body ?? '-',
        recipients: recipients.join(', ') || '-',
        sentAt: sentAtValue > 0 ? moment(createdAt).format('MM/DD/YYYY') : '-',
        sentAtValue,
      };
    })
    .sort((a, b) => b.sentAtValue - a.sentAtValue);

const getNormalizedThreadMessages = (items) =>
  items
    .map((item) => {
      const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
      const sentAtValue =
        createdAt && moment(createdAt).isValid() ? moment(createdAt).valueOf() : 0;
      return {
        id: item.id,
        body: item.body ?? '-',
        senderType: item.senderType ?? item.sender_type ?? '',
        attachmentId: item.attachmentId ?? item.attachment_id ?? null,
        sentAt: sentAtValue > 0 ? moment(createdAt).format('MM/DD/YYYY HH:mm') : '-',
        sentAtValue,
      };
    })
    .sort((a, b) => a.sentAtValue - b.sentAtValue);

const getTableRows = (threads) =>
  threads.map((item) => {
    const subject = item?.subject?.props?.text ?? item?.subject ?? '-';
    const recipients = item?.recipients?.props?.text ?? item?.recipients ?? '-';
    const sentAt = item?.sentAt?.props?.text ?? item?.sentAt ?? '-';
    const sentAtValue =
      item?.sentAtValue ??
      item?.sentAt?.props?.value ??
      (sentAt !== '-' && moment(sentAt, 'MM/DD/YYYY', true).isValid()
        ? moment(sentAt, 'MM/DD/YYYY', true).valueOf()
        : -1);

    return {
      subject: <TextItem isActive text={subject} />,
      recipients: <TextItem isActive text={recipients} />,
      sentAt: <TextItem isActive text={sentAt} value={sentAtValue} />,
      actions: (
        <ActionsRow
          options={[
            {
              id: `view-conversation-button-${item.threadId}`,
              text: 'View Conversation',
              backgroundColor: '#555671B2',
              color: 'white',
              onClick: item.onView,
            },
          ]}
        />
      ),
    };
  });

const SecureMessagingAll = ({ patientId }) => {
  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();
  const cachedThreads = useCustomSelector((state) => getCachedPortalThreads(state, patientId));
  const [threads, setThreads] = useState(cachedThreads || []);
  const [isLoading, setIsLoading] = useState(!cachedThreads);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [selectedThread, setSelectedThread] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const cachedThreadMessagesById = useCustomSelector(
    (state) => state.cache.portalThreadMessages || {},
  );

  useEffect(() => {
    if (cachedThreads) {
      setThreads(cachedThreads || []);
      setIsLoading(false);
    }
  }, [cachedThreads]);

  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const response = await apiRequest({
          endpoint: `patientPortalMessages/patient/${patientId}/threads`,
        });

        if (!response.ok) {
          const err = await response.json();
          showAlert(AlertType.Error, '', getRestErrorMessage(err) || 'Failed to fetch threads');
          return;
        }

        const data = await response.json();
        const payload =
          data?.PatientPortalMessage ??
          data?.patientPortalMessage ??
          data?.patient_portal_message ??
          [];
        const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
        const normalizedThreads = getNormalizedThreads(items);

        dispatch(cachePortalThreads(patientId, normalizedThreads));
        setThreads(normalizedThreads);
      } catch (error) {
        showAlert(AlertType.Error, '', error.message || 'Failed to fetch threads');
      } finally {
        setIsLoading(false);
      }
    };

    fetchThreads();
  }, [patientId, dispatch]);

  useEffect(() => {
    if (!selectedThread?.threadId) return;
    const cachedMessages = cachedThreadMessagesById[selectedThread.threadId];
    if (cachedMessages) {
      setConversationMessages(cachedMessages);
      setIsConversationLoading(false);
    }
  }, [selectedThread?.threadId, cachedThreadMessagesById]);

  const onOpenConversation = async (thread) => {
    setSelectedThread(thread);
    const cachedMessages = cachedThreadMessagesById[thread.threadId];
    if (cachedMessages) {
      setConversationMessages(cachedMessages);
    }

    setIsConversationLoading(!cachedMessages);
    try {
      const response = await apiRequest({
        endpoint: `patientPortalMessages/thread/${thread.threadId}`,
      });
      if (!response.ok) {
        const err = await response.json();
        showAlert(AlertType.Error, '', getRestErrorMessage(err) || 'Failed to fetch conversation');
        return;
      }

      const data = await response.json();
      const payload =
        data?.PatientPortalMessage ??
        data?.patientPortalMessage ??
        data?.patient_portal_message ??
        [];
      const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
      const normalizedMessages = getNormalizedThreadMessages(items);
      dispatch(cachePortalThreadMessages(thread.threadId, normalizedMessages));
      setConversationMessages(normalizedMessages);
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to fetch conversation');
    } finally {
      setIsConversationLoading(false);
    }
  };

  const onDownloadAttachment = async (attachmentId) => {
    if (!attachmentId) return;
    try {
      let fileName = `attachment-${attachmentId}`;
      const response = await apiRequest({ endpoint: `patientDocuments/${attachmentId}` });
      if (response.ok) {
        const data = await response.json();
        fileName =
          data?.PatientDocument?.fileName ??
          data?.patientDocument?.fileName ??
          data?.patient_document?.file_name ??
          fileName;
      }
      await openDocument(attachmentId, fileName, (error) => {
        showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
      });
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
    }
  };

  if (isLoading) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling threads..." />;
  }

  return (
    <div className={styles.container}>
      <h3 className={[styles.title, 'font-s-24', 'font-w-700'].join(' ')}>All Messages</h3>
      <BaseTable
        headers={TableHeaders}
        data={getTableRows(
          threads.map((thread) => ({
            ...thread,
            onView: () => onOpenConversation(thread),
          })),
        )}
        name="secure-messages"
        classNames={[styles.table, threads?.length <= ResultsCount[25] ? styles.noPagination : '']}
        emptyText="No threads"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'sentAt',
          label: 'Sent At',
          width: '10%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination
        onChangePageHandler={setPaginationData}
        results={threads}
        selectedTab="secure-messaging-all"
        containerClassNames={[styles.pagination]}
      />
      {!!selectedThread && (
        <BasePopup
          id="secure-thread-conversation"
          open
          onClose={() => {
            setSelectedThread(null);
            setConversationMessages([]);
          }}
          title={selectedThread.subject || 'Conversation'}
        >
          <div className={styles.messagePopupBody}>
            {isConversationLoading && <div>Loading conversation...</div>}
            {!isConversationLoading && !conversationMessages.length && <div>No messages</div>}
            {!isConversationLoading &&
              conversationMessages.map((message) => {
                const isClinic = `${message.senderType}`.toUpperCase() === 'CLINIC';
                return (
                  <div
                    key={message.id || `${message.sentAt}-${message.body}`}
                    className={[
                      styles.bubbleRow,
                      isClinic ? styles.bubbleRowRight : styles.bubbleRowLeft,
                    ].join(' ')}
                  >
                    <div
                      className={[
                        styles.bubble,
                        isClinic ? styles.bubbleClinic : styles.bubbleOther,
                      ].join(' ')}
                    >
                      <div>{message.body}</div>
                      {!!message.attachmentId && (
                        <button
                          type="button"
                          className={styles.attachmentButton}
                          onClick={() => onDownloadAttachment(message.attachmentId)}
                        >
                          Download attachment
                        </button>
                      )}
                      <div className={styles.bubbleMeta}>{message.sentAt}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        </BasePopup>
      )}
    </div>
  );
};

SecureMessagingAll.propTypes = {
  patientId: PropTypes.number.isRequired,
};

export default SecureMessagingAll;
